import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL  = 5 * 60 * 1000
const STAT_NAMES = ['created_bookings', 'completed_bookings', 'rescheduled_bookings', 'canceled_bookings']

export const useAnalyticsBookingsStore = defineStore('analyticsBookings', {
  state: () => ({
    // Stat cards  { [duration]: { created_bookings, completed_bookings, ... } }
    stats:          {},
    statsFetchedAt: {},
    statsLoading:   false,
    statsError:     null,

    // Highcharts series data  { [duration]: { series, drilldown } }
    popularDays:          {},
    popularDaysFetchedAt: {},
    popularDaysLoading:   false,
    popularDaysError:     null,

    // Top events  { [duration]: { range, stats: [] } }
    popularEvents:          {},
    popularEventsFetchedAt: {},
    popularEventsLoading:   false,
    popularEventsError:     null,

    // Top attendees  { [duration]: { range, stats: [] } }
    topAttendees:          {},
    topAttendeesFetchedAt: {},
    topAttendeesLoading:   false,
    topAttendeesError:     null,

    // Bookings over time  { [duration]: { labels, created, completed, canceled } }
    bookingsOverTime:          {},
    bookingsOverTimeFetchedAt: {},
    bookingsOverTimeLoading:   false,
    bookingsOverTimeError:     null,
  }),

  getters: {
    isStatsCacheValid:            (s) => (d) => s.statsFetchedAt[d]            != null && Date.now() - s.statsFetchedAt[d]            < CACHE_TTL,
    isPopularDaysCacheValid:      (s) => (d) => s.popularDaysFetchedAt[d]      != null && Date.now() - s.popularDaysFetchedAt[d]      < CACHE_TTL,
    isPopularEventsCacheValid:    (s) => (d) => s.popularEventsFetchedAt[d]    != null && Date.now() - s.popularEventsFetchedAt[d]    < CACHE_TTL,
    isTopAttendeesCacheValid:     (s) => (d) => s.topAttendeesFetchedAt[d]     != null && Date.now() - s.topAttendeesFetchedAt[d]     < CACHE_TTL,
    isBookingsOverTimeCacheValid: (s) => (d) => s.bookingsOverTimeFetchedAt[d] != null && Date.now() - s.bookingsOverTimeFetchedAt[d] < CACHE_TTL,
  },

  actions: {
    async fetchAll(duration) {
      const fetches = []
      if (!this.isStatsCacheValid(duration))            fetches.push(this.fetchStats(duration))
      if (!this.isPopularDaysCacheValid(duration))      fetches.push(this.fetchPopularDays(duration))
      if (!this.isPopularEventsCacheValid(duration))    fetches.push(this.fetchPopularEvents(duration))
      if (!this.isTopAttendeesCacheValid(duration))     fetches.push(this.fetchTopAttendees(duration))
      if (!this.isBookingsOverTimeCacheValid(duration)) fetches.push(this.fetchBookingsOverTime(duration))
      await Promise.allSettled(fetches)
    },

    async fetchStats(duration) {
      this.statsLoading = true
      this.statsError   = null
      const { axiosRequest } = useRequest()
      const result = {}
      await Promise.allSettled(
        STAT_NAMES.map(async (name) => {
          const res = await axiosRequest('GET', `/analytics/${name}?duration=${duration}`, {}, false)
          result[name] = (!res.js_error && res.response?.success) ? res.response.data : null
        })
      )
      this.stats[duration]          = result
      this.statsFetchedAt[duration] = Date.now()
      this.statsLoading             = false
    },

    async fetchPopularDays(duration) {
      this.popularDaysLoading = true
      this.popularDaysError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/events/popular_days_of_week?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.popularDays[duration] = res.response.data.stats
      } else {
        this.popularDaysError = 'Failed to load booking activity.'
      }
      this.popularDaysFetchedAt[duration] = Date.now()
      this.popularDaysLoading             = false
    },

    async fetchPopularEvents(duration) {
      this.popularEventsLoading = true
      this.popularEventsError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/events/popular_events?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.popularEvents[duration] = res.response.data
      } else {
        this.popularEventsError = 'Failed to load popular events.'
      }
      this.popularEventsFetchedAt[duration] = Date.now()
      this.popularEventsLoading             = false
    },

    async fetchTopAttendees(duration) {
      this.topAttendeesLoading = true
      this.topAttendeesError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/events/users_with_the_most_bookings?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.topAttendees[duration] = res.response.data
      } else {
        this.topAttendeesError = 'Failed to load top attendees.'
      }
      this.topAttendeesFetchedAt[duration] = Date.now()
      this.topAttendeesLoading             = false
    },

    async fetchBookingsOverTime(duration) {
      this.bookingsOverTimeLoading = true
      this.bookingsOverTimeError   = null
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(
        'GET', `/api/v1/analytics/events/bookings_over_time?duration=${duration}`, {}, false
      )
      if (!res.js_error && res.response?.success) {
        this.bookingsOverTime[duration] = res.response.data
      } else {
        this.bookingsOverTimeError = 'Failed to load booking trend data.'
      }
      this.bookingsOverTimeFetchedAt[duration] = Date.now()
      this.bookingsOverTimeLoading             = false
    },

    invalidate(duration) {
      if (duration) {
        this.statsFetchedAt[duration]            = null
        this.popularDaysFetchedAt[duration]      = null
        this.popularEventsFetchedAt[duration]    = null
        this.topAttendeesFetchedAt[duration]     = null
        this.bookingsOverTimeFetchedAt[duration] = null
      } else {
        this.statsFetchedAt            = {}
        this.popularDaysFetchedAt      = {}
        this.popularEventsFetchedAt    = {}
        this.topAttendeesFetchedAt     = {}
        this.bookingsOverTimeFetchedAt = {}
      }
    },
  },
})
